/*
  barna flow — S2 cart drawer behaviour (sections/cart-drawer.liquid).

  assets/cart.js still owns opening, closing, quantity, remove and re-rendering the drawer.
  This file adds only what it lacks:
  - <s2-cart-add>     one-tap add on a cross-sell card
  - <s2-cart-toggle>  the add-on switch (adds the product, or removes its line)
  - <s2-cart-rail>    carousel arrows for desktop, where a mouse can't swipe
  - Escape to close, focus moved into the drawer on open
  - opening the drawer while its section or a block is selected in the theme editor
*/

(() => {
  if (customElements.get('s2-cart-add')) return;

  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const getDrawer = () => document.querySelector('m-cart-drawer');
  const getItems = () => document.getElementById('MinimogCartDrawerBody');

  // Ask every cart-aware component which sections it wants back (drawer, header count...).
  const bundledSections = () => {
    const sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections } })
    );
    return [...new Set(sections)];
  };

  const cartRequest = async (path, payload) => {
    const response = await fetch(`${root}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        ...payload,
        sections: bundledSections(),
        sections_url: window.location.pathname,
      }),
    });
    const json = await response.json();
    if (!response.ok || json.status) {
      throw new Error(json.description || json.message || 'Something went wrong');
    }
    return json;
  };

  // Same event the theme's product form emits: cart.js re-renders the drawer and the count.
  // The cart has already changed by now, so a subscriber that throws (a theme or app script)
  // is logged, not reported to the shopper as a failed add.
  const publish = (cart) => {
    try {
      window.MinimogEvents?.emit(window.MinimogTheme.pubSubEvents.cartUpdate, { cart });
    } catch (error) {
      console.error('[s2-cart-drawer] cart update subscriber failed', error);
    }
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
  };

  const withLoading = async (task) => {
    const items = getItems();
    items?.loading?.start();
    try {
      return await task();
    } finally {
      items?.loading?.finish();
    }
  };

  const notifyError = (target, message) => {
    // The drawer may have re-rendered and detached the element that asked.
    const anchor = target && target.isConnected ? target : getItems();
    if (anchor && window.MinimogTheme?.Notification) {
      window.MinimogTheme.Notification.show({ target: anchor, method: 'appendChild', type: 'error', message, last: 3000 });
    } else {
      console.error(message);
    }
  };

  class S2CartAdd extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('button');
      this.onClick = this.add.bind(this);
      this.button?.addEventListener('click', this.onClick);
    }

    disconnectedCallback() {
      this.button?.removeEventListener('click', this.onClick);
    }

    async add() {
      if (this.button.getAttribute('aria-busy') === 'true') return;
      this.button.setAttribute('aria-busy', 'true');
      let cart;
      try {
        cart = await withLoading(async () => {
          const added = await cartRequest('cart/add.js', { items: [{ id: Number(this.dataset.variantId), quantity: 1 }] });
          // assets/s4-cart-bundle.js puts the new pair into the cart's Kaching bundle before the
          // drawer redraws, so the price shown is the bundle's from the start; when it changed
          // anything it has already redrawn the drawer itself.
          if (await window.s4CartBundle?.regroup?.()) return null;
          return added;
        });
      } catch (error) {
        this.button.removeAttribute('aria-busy');
        notifyError(this, error.message);
        return;
      }
      if (cart) publish(cart);
    }
  }

  class S2CartToggle extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="checkbox"]');
      this.onChange = this.toggle.bind(this);
      this.input?.addEventListener('change', this.onChange);
    }

    disconnectedCallback() {
      this.input?.removeEventListener('change', this.onChange);
    }

    async toggle(event) {
      // cart.js listens for change on the whole body; this one is not a quantity input.
      event.stopPropagation();
      const turningOn = this.input.checked;
      this.input.disabled = true;
      let cart;
      try {
        cart = await withLoading(() =>
          turningOn || !this.dataset.lineKey
            ? cartRequest('cart/add.js', { items: [{ id: Number(this.dataset.variantId), quantity: 1 }] })
            : cartRequest('cart/change.js', { id: this.dataset.lineKey, quantity: 0 })
        );
      } catch (error) {
        this.input.checked = !turningOn;
        this.input.disabled = false;
        notifyError(this, error.message);
        return;
      }
      publish(cart);
    }
  }

  class S2CartRail extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-rail-track]');
      this.prev = this.querySelector('[data-rail-prev]');
      this.next = this.querySelector('[data-rail-next]');
      if (!this.track || !this.prev || !this.next) return;

      this.onPrev = () => this.step(-1);
      this.onNext = () => this.step(1);
      this.onScroll = () => this.sync();
      this.prev.addEventListener('click', this.onPrev);
      this.next.addEventListener('click', this.onNext);
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      // The drawer is closed (no width) when this is built, so the arrows check again whenever the
      // track gets its size: when the drawer opens, and when the screen is resized.
      this.resize = new ResizeObserver(this.onScroll);
      this.resize.observe(this.track);
      this.sync();
    }

    disconnectedCallback() {
      this.prev?.removeEventListener('click', this.onPrev);
      this.next?.removeEventListener('click', this.onNext);
      this.track?.removeEventListener('scroll', this.onScroll);
      this.resize?.disconnect();
    }

    step(direction) {
      const card = this.track.firstElementChild;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      const distance = card ? card.getBoundingClientRect().width + gap : this.track.clientWidth;
      this.track.scrollBy({ left: direction * distance, behavior: 'smooth' });
    }

    sync() {
      // Nothing to measure while the drawer is closed; leave the arrows usable.
      if (!this.track.clientWidth) return;
      const max = this.track.scrollWidth - this.track.clientWidth;
      this.prev.disabled = this.track.scrollLeft <= 2;
      this.next.disabled = this.track.scrollLeft >= max - 2;
    }
  }

  customElements.define('s2-cart-add', S2CartAdd);
  customElements.define('s2-cart-toggle', S2CartToggle);
  customElements.define('s2-cart-rail', S2CartRail);

  const isOpen = () => getDrawer()?.classList.contains('m-cart-drawer--active');

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape' || !isOpen()) return;
    const drawer = getDrawer();
    drawer.close();
    drawer.activeElement?.focus?.({ preventScroll: true });
  });

  const focusDrawer = () => {
    window.setTimeout(() => {
      if (!isOpen()) return;
      getDrawer().querySelector('.m-cart-drawer__close')?.focus({ preventScroll: true });
    }, 360);
  };

  const subscribeOpen = () => {
    if (window.MinimogEvents && window.MinimogTheme?.pubSubEvents) {
      window.MinimogEvents.subscribe(window.MinimogTheme.pubSubEvents.openCartDrawer, focusDrawer);
    }
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', subscribeOpen);
  } else {
    subscribeOpen();
  }

  // Theme editor: show the drawer while the merchant is editing it.
  if (window.Shopify && window.Shopify.designMode) {
    const isThisSection = (event) => event.detail && event.detail.sectionId === 'cart-drawer';
    const open = (event) => {
      if (isThisSection(event)) window.setTimeout(() => getDrawer()?.open(), 50);
    };
    document.addEventListener('shopify:section:select', open);
    document.addEventListener('shopify:section:load', open);
    document.addEventListener('shopify:block:select', open);
    document.addEventListener('shopify:section:deselect', (event) => {
      if (isThisSection(event)) getDrawer()?.close();
    });
  }
})();
